import React, { useState, useEffect, useMemo, useCallback } from 'react';
import interviewimg from "./interviewimg.png";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import SkillCard from './SkillCard';
import { useStaticJobs } from './useStaticJobs';
import JobDashboard from './JobDashboard';
import { MapPin, Building2, DollarSign, Tag, ExternalLink, Trash2, History } from 'lucide-react';
import { apiCall, getErrorMessage } from './utils/api';
import { collection, query, orderBy, onSnapshot } from 'firebase/firestore';

// NEW imports: InterviewHistoryCard + hook
import { useInterviewSessions } from './InterviewHistoryCard';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

// Small CertificateCard component
function CertificateCard({ cert }) {
  const handleClick = () => {
    if (cert?.link) {
      window.open(cert.link, "_blank");
    }
  };

  return (
    <div
      onClick={handleClick}
      className="bg-gradient-to-br from-white to-black-50 border border-black-100 rounded-2xl p-4 shadow-sm hover:shadow-md transition-shadow duration-150 cursor-pointer"
    >
      <div className="flex items-start gap-3">
        <div className="flex-shrink-0 w-12 h-12 rounded-lg bg-blue-50 flex items-center justify-center text-blue-600 font-semibold">
          🎓
        </div>
        <div className="flex-1">
          <div className="text-sm font-semibold text-black-800">
            {cert?.name || "Untitled Cert"}
          </div>
          {cert?.issuedAt && (
            <div className="text-xs text-black-400 mt-1">
              Issued: {new Date(cert.issuedAt).toLocaleDateString()}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ---------- helpers ----------
function isTodaySession(session) {
  if (!session?.createdAt?.seconds) return false;
  const timestamp = session.createdAt.seconds * 1000 + Math.floor((session.createdAt.nanoseconds || 0) / 1e6);
  const date = new Date(timestamp);
  const today = new Date();
  return (
    date.getFullYear() === today.getFullYear() &&
    date.getMonth() === today.getMonth() &&
    date.getDate() === today.getDate()
  );
}

function normalizeSkills(skills) {
  if (Array.isArray(skills)) {
    return skills.map(String).map(s => s.trim()).filter(Boolean).sort();
  }
  if (typeof skills === "string") {
    return skills.split(",").map(s => s.trim()).filter(Boolean).sort();
  }
  try {
    return JSON.stringify(skills);
  } catch {
    return String(skills ?? "");
  }
}

function makeSkillsSignature(skills) {
  const normalized = normalizeSkills(skills);
  return Array.isArray(normalized) ? normalized.join(",") : String(normalized);
}

function formatResumeDataAsText(data) {
  if (!data || !data.personalInfo) return "";
  let text = `Name: ${data.personalInfo.name}\nEmail: ${data.personalInfo.email}\nPhone: ${data.personalInfo.phone}\nLocation: ${data.personalInfo.location}\n\n`;
  if (data.goal) text += `## Goal\n${data.goal}\n\n`;
  text += "## Work Experience\n";
  (data.experience || []).forEach(exp => {
    text += `- ${exp.title} at ${exp.company} (${exp.period})\n  ${exp.description}\n`;
  });
  text += "\n## Education\n";
  (data.education || []).forEach(edu => {
    text += `- ${edu.degree} from ${edu.school} (${edu.period})\n  ${edu.description}\n`;
  });
  text += "\n## Skills\n";
  const skills = Array.isArray(data.skills)
    ? data.skills.filter(Boolean).map(String)
    : (typeof data.skills === 'string'
      ? data.skills.split(',').map(s => s.trim()).filter(Boolean)
      : []);
  text += skills.join(', ');
  return text;
}

function buildCacheKey(appId, uid, signature, timestamp) {
  return `recommendedSkillsCache:${appId || "app"}:${uid || "anon"}:${signature}:${timestamp || 'no-ts'}`;
}

function readCache(key) {
  if (typeof window === "undefined") return null;
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    const ttlMs = 24 * 60 * 60 * 1000;
    if (Date.now() - (parsed.ts || 0) > ttlMs) return null;
    return parsed.data || null;
  } catch {
    return null;
  }
}

function writeCache(key, data) {
  if (typeof window === "undefined") return;
  try {
    localStorage.setItem(key, JSON.stringify({ ts: Date.now(), data }));
  } catch (err) {
    console.error("Error writing cache:", err);
  }
}

function ViewSessionDetailModal({ session, onClose }) {
  const [activeTab, setActiveTab] = useState("review");
  if (!session) return null;
  const transcriptArray = session.transcript ? Object.entries(session.transcript) : [];
  const createdAt = session?.createdAt?.seconds ? new Date(session.createdAt.seconds * 1000).toLocaleString() : "N/A";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
      <div className="w-full max-w-4xl max-h-[90vh] bg-[#0B0B0B] opacity-100 backdrop-blur-none border border-black-700 rounded-2xl shadow-2xl flex flex-col">
        <div className="p-6 border-b border-black-700 flex justify-between">
          <div>
            <h3 className="text-xl font-bold text-white">{session.title || "Interview Session"}</h3>
            <p className="text-sm text-gray-400 mt-1">Session from {createdAt}</p>
          </div>
          <button onClick={onClose} className="px-4 py-2 rounded-lg bg-black-700 text-white hover:bg-black-600">Close</button>
        </div>
        <div className="px-6 border-b border-black-700">
          <div className="flex gap-6 -mb-px">
            {["review", "qna"].map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`py-3 border-b-2 text-sm font-semibold ${activeTab === tab ? "border-emerald-500 text-emerald-400" : "border-transparent text-gray-500 hover:text-gray-300"}`}
              >
                {tab === "review" ? "Session Review" : "AI Q&A"}
              </button>
            ))}
          </div>
        </div>
        <div className="p-6 overflow-y-auto text-white">
          {activeTab === "review" ? (
            <>
              <h4 className="text-lg font-bold mb-3">Final Evaluation</h4>
              <div className="bg-black-800 border border-black-700 rounded-lg p-4 mb-6 whitespace-pre-wrap">{session.evaluation || "No evaluation available."}</div>
              <h4 className="text-lg font-bold mb-3">Interview Transcript</h4>
              <div className="space-y-4">
                {transcriptArray.length ? transcriptArray.map(([q, a], i) => (
                  <div key={i} className="bg-black-800 rounded-lg p-4">
                    <p className="font-semibold text-gray-200">Q: {q}</p>
                    <p className="text-gray-400 mt-1">A: {a}</p>
                  </div>
                )) : <p className="text-gray-500">No transcript available.</p>}
              </div>
            </>
          ) : (
            <>
              <h4 className="text-lg font-bold mb-4">AI Generated Q&A</h4>
              {session.qna_list?.length ? (
                <div className="space-y-4">
                  {session.qna_list.map((item, i) => (
                    <div key={i} className="bg-black-800 rounded-lg p-4">
                      <p className="font-semibold text-gray-200">Q: {item.question}</p>
                      <p className="text-gray-400 mt-2 whitespace-pre-wrap">A: {item.answer}</p>
                    </div>
                  ))}
                </div>
              ) : <p className="text-gray-500">No Q&A available.</p>}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ---------- component ----------
export default function Dashboard({
  user,
  userData,
  resumeData,
  db,
  appId,
  onGenerateCoverLetter,
  onGenerateInterviewQuestions,
  onViewSavedQna,   
  setActiveTab
}) {
  const { sessions: sessionHistory = [], isLoading: isHistoryLoading, error: historyError } = useInterviewSessions(db, appId, user?.uid);

  const [error, setError] = useState(null);
  const [recommendedSkills, setRecommendedSkills] = useState([]);
  const [viewingSession, setViewingSession] = useState(null);
  const [savedJobs, setSavedJobs] = useState(() => {
    try { return JSON.parse(localStorage.getItem('savedJobs')) || []; } catch { return []; }
  });
  const [usageTodayMin, setUsageTodayMin] = useState(0);
  const [usageTotalMin, setUsageTotalMin] = useState(0);
  const [isFetchingFullJD, setIsFetchingFullJD] = useState(false);
  const [matchDetails, setMatchDetails] = useState({});
  const [loadingJobs, setLoadingJobs] = useState({});
  const [coverLetterJobId, setCoverLetterJobId] = useState(null);
  const [loadingQuestions, setLoadingQuestions] = useState({});
  const [qnaHistory, setQnaHistory] = useState([]);

  const { jobs } = useStaticJobs(resumeData?.title, "us", 1);

  const todaysSessions = useMemo(() => (sessionHistory || []).filter(isTodaySession), [sessionHistory]);
  const skillsSignature = useMemo(() => makeSkillsSignature(resumeData?.skills), [resumeData?.skills]);
  const cacheKey = useMemo(() => buildCacheKey(appId, user?.uid, skillsSignature, resumeData?.updatedAt), [appId, user?.uid, skillsSignature, resumeData?.updatedAt]);

  useEffect(() => {
    if (!user?.uid) return;
    const q = query(collection(db, `users/${user.uid}/saved_qna`), orderBy("createdAt", "desc"));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      setQnaHistory(snapshot.docs.map(d => ({ id: d.id, ...d.data() })));
    });
    return () => unsubscribe();
  }, [user?.uid, db]);

  useEffect(() => {
    let cancelled = false;
    async function getSkillRecommendations() {
      if (!resumeData?.skills) return;
      const cached = readCache(cacheKey);
      if (cached && !cancelled) {
        setRecommendedSkills(cached);
        return;
      }
      try {
        const { data } = await apiCall('/api/skill-recommendation', {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ resume_text: formatResumeDataAsText(resumeData) }),
        });
        if (!cancelled) {
          setRecommendedSkills(data);
          writeCache(cacheKey, data);
        }
      } catch (err) { console.error("Recommendation error:", err); }
    }
    getSkillRecommendations();
    return () => { cancelled = true; };
  }, [userData, resumeData?.skills, cacheKey]);

  useEffect(() => {
    if (!user?.uid) return;
    const key = `usage:${appId}:${user.uid}`;
    const update = () => {
      try {
        const data = JSON.parse(localStorage.getItem(key)) || { totalMs: 0, days: {} };
        const todayKey = new Date().toISOString().slice(0, 10);
        setUsageTodayMin(Math.max(0, Math.round((data.days?.[todayKey] || 0) / 60000)));
        setUsageTotalMin(Math.max(0, Math.round((data.totalMs || 0) / 60000)));
      } catch { /* ignore */ }
    };
    update();
    window.addEventListener('usage-updated', update);
    return () => window.removeEventListener('usage-updated', update);
  }, [user?.uid, appId]);

  useEffect(() => {
    const refresh = () => {
      try { setSavedJobs(JSON.parse(localStorage.getItem('savedJobs')) || []); } catch { setSavedJobs([]); }
    };
    window.addEventListener('storage', refresh);
    window.addEventListener('saved-jobs-updated', refresh);
    return () => {
      window.removeEventListener('storage', refresh);
      window.removeEventListener('saved-jobs-updated', refresh);
    };
  }, []);

  const handleViewMatchScore = async (job) => {
    if (matchDetails[job.id] || loadingJobs[job.id]) return;
    setLoadingJobs(prev => ({ ...prev, [job.id]: true }));
    try {
      const formData = new FormData();
      formData.append('resume_text', formatResumeDataAsText(resumeData));
      formData.append('job_description', job.description || '');
      const { data } = await apiCall('/api/jobs/match-details', { method: 'POST', body: formData });
      setMatchDetails(prev => ({ ...prev, [job.id]: data }));
    } catch (error) {
      setMatchDetails(prev => ({ ...prev, [job.id]: { error: error.message, match_score: 0 } }));
    } finally { setLoadingJobs(prev => ({ ...prev, [job.id]: false })); }
  };

  const handleGenerateCoverLetter = async (job) => {
    if (!onGenerateCoverLetter) return;
    setCoverLetterJobId(job.id);
    setIsFetchingFullJD(true);
    try {
      const { data } = await apiCall('/api/jobs/get-full-description', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(job),
      });
      onGenerateCoverLetter(data.full_description || job.description);
      setActiveTab('coverLetter');
    } catch (error) {
      onGenerateCoverLetter(job.description);
      setActiveTab('coverLetter');
    } finally {
      setIsFetchingFullJD(false);
      setCoverLetterJobId(null);
    }
  };

  const handleInterviewQuestions = useCallback(async (job) => {
    if (!onGenerateInterviewQuestions) return;
    setLoadingQuestions(prev => ({ ...prev, [job.id]: true }));
    try {
      const { data } = await apiCall('/api/jobs/get-full-description', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(job),
      });
      onGenerateInterviewQuestions(data.full_description || job.description);
      setActiveTab('interviewQuestions');
    } catch (error) {
      onGenerateInterviewQuestions(job.description);
      setActiveTab('interviewQuestions');
    } finally { setLoadingQuestions(prev => ({ ...prev, [job.id]: false })); }
  }, [onGenerateInterviewQuestions, setActiveTab]);

  const removeSavedJob = (id) => {
    const next = (savedJobs || []).filter(j => j.id !== id);
    localStorage.setItem('savedJobs', JSON.stringify(next));
    setSavedJobs(next);
    window.dispatchEvent(new Event('saved-jobs-updated'));
  };

  const { skillList, certificateList } = useMemo(() => {
    const skills = [];
    const certs = [];
    (recommendedSkills || []).forEach((item) => {
      if (!item) return;
      if (item.type === 'certificate' || item.issuer || /cert/i.test(item.name || '')) {
        certs.push(item);
      } else { skills.push(item); }
    });
    return { skillList: skills, certificateList: certs };
  }, [recommendedSkills]);

  return (
    <div className="p-6 bg-black-50 min-h-screen overflow-x-hidden">
      <div className="mb-6">
        <h1 className="text-xl font-semibold">Welcome back, <span className="capitalize">{userData.displayName}👋</span></h1>
        <p className="text-sm text-black-500">Your progress this week is awesome. Let’s keep it up!</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-lg shadow p-4">
          <p className="text-sm text-zinc-900 font-medium">Today's Minutes</p>
          <p className="text-lg font-bold text-zinc-800">{usageTodayMin}m</p>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <p className="text-sm text-zinc-900 font-medium">Expected Success</p>
          <p className="text-lg font-bold text-blue-600">82%</p>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <p className="text-sm text-zinc-900 font-medium">Interviews Done</p>
          <p className="text-lg font-bold text-yellow-600">{sessionHistory.length}</p>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <p className="text-sm text-zinc-900 font-medium">Total Minutes</p>
          <p className="text-lg font-bold text-zinc-800">{usageTotalMin}m</p>
        </div>
      </div>

      {/* Recommended Skills section */}
      <div className="mb-6">
        <h3 className="text-lg font-semibold text-black-700 mb-4">Recommended for You</h3>
        <div className="bg-white rounded-2xl p-6 shadow-sm border border-black-100 mb-6">
          <h4 className="text-md font-semibold text-zinc-900">Top Skills</h4>
          <p className="text-xs text-zinc-500 mb-4">Boost your match rate with these skills</p>
          {skillList.length === 0 ? <p className="text-sm text-black-400">No skills found.</p> : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {skillList.slice(0, 5).map((skill, idx) => <SkillCard key={idx} skill={skill} />)}
            </div>
          )}
        </div>
        <div className="bg-white rounded-2xl p-6 shadow-sm border border-black-100">
          <h4 className="text-md font-semibold text-zinc-900">Certifications</h4>
          <p className="text-xs text-zinc-500 mb-4">Creds that boost your profile</p>
          {certificateList.length === 0 ? <p className="text-sm text-black-400">No certs found.</p> : (
            <div className="flex flex-col gap-3">
              {certificateList.map((cert, i) => <CertificateCard key={i} cert={cert} />)}
            </div>
          )}
        </div>
      </div>

      {/* Saved Jobs section */}
      <div className="bg-white rounded-2xl shadow p-6 mb-6 border border-black-100">
        <h2 className="text-lg font-semibold mb-4 text-zinc-900">Saved Jobs</h2>
        {savedJobs.length === 0 ? <p className="text-black-400 text-sm">No saved jobs yet.</p> : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
            {savedJobs.map((job) => (
              <div key={job.id} className="group p-6 rounded-2xl border-2 bg-white border-gray-200 hover:border-emerald-300 hover:shadow-xl transition-all">
                <div className="flex justify-between items-start gap-3">
                  <div className="flex-1 min-w-0">
                    <h3 className="text-lg font-bold text-gray-900 truncate">{job.title}</h3>
                    <div className="flex items-center gap-2 mt-1">
                      <Building2 size={14} className="text-gray-400" />
                      <p className="text-gray-700 font-semibold truncate text-sm">{job.company}</p>
                    </div>
                  </div>
                  {job.category && (
                    <span className="bg-emerald-50 text-emerald-700 text-xs font-bold px-3 py-1.5 rounded-full flex items-center gap-1.5">
                      <Tag size={12} /> {job.category}
                    </span>
                  )}
                </div>

                <div className="text-sm text-gray-600 flex flex-wrap items-center gap-x-4 gap-y-2 mt-4">
                  <span className="flex items-center gap-1.5"><MapPin size={14} className="text-emerald-500" />{job.location}</span>
                  <span className="flex items-center gap-1.5"><DollarSign size={14} className="text-green-600" />Not disclosed</span>
                </div>

                <div className="mt-4 pt-4 border-t border-gray-100 space-y-3">
                  <div className="grid grid-cols-3 gap-2">
                    {matchDetails[job.id] ? (
                      <div className="p-2 bg-emerald-50 rounded-xl text-center">
                        <p className="text-[10px] font-semibold text-emerald-700">Match</p>
                        <p className="text-sm font-bold text-emerald-600">{matchDetails[job.id].match_score}%</p>
                      </div>
                    ) : (
                      <button onClick={() => handleViewMatchScore(job)} className="bg-white text-emerald-600 rounded-xl py-2 text-xs font-semibold border border-emerald-100">Score</button>
                    )}
                    <button onClick={() => handleGenerateCoverLetter(job)} className="bg-white text-emerald-600 rounded-xl py-2 text-xs font-semibold border border-emerald-100">CL</button>
                    <button onClick={() => handleInterviewQuestions(job)} className="bg-white text-emerald-600 rounded-xl py-2 text-xs font-semibold border border-emerald-100">Q&A</button>
                  </div>
                  <div className="flex items-center justify-between">
                    <a href={job.redirect_url} target="_blank" rel="noreferrer" className="bg-emerald-500 text-white font-bold py-2 px-6 rounded-xl text-xs">Apply Now</a>
                    <button onClick={() => removeSavedJob(job.id)} className="p-2 text-red-500 hover:bg-red-50 rounded-full transition-colors"><Trash2 size={18}/></button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Insights Table */}
      <div className="bg-white rounded-lg shadow p-4 mb-6">
        <h2 className="text-lg font-semibold mb-4 text-zinc-900">Interview Insights</h2>
        <table className="w-full text-sm text-left">
          <thead>
            <tr className="border-b text-zinc-900 font-semibold">
              <th className="py-2 px-2">Role</th>
              <th className="py-2 px-2">Date</th>
              <th className="py-2 px-2">Action</th>
            </tr>
          </thead>
          <tbody>
            {sessionHistory.length === 0 ? <tr><td colSpan="3" className="py-4 text-center text-black-400">No sessions.</td></tr> : (
              sessionHistory.slice(0, 5).map((s) => (
                <tr key={s.id} className="border-b hover:bg-gray-50">
                  <td className="py-2 px-2 text-zinc-900 font-medium">{s.title || "Untitled"}</td>
                  <td className="py-2 px-2 text-zinc-700">{s.createdAt?.seconds ? new Date(s.createdAt.seconds * 1000).toLocaleDateString() : "-"}</td>
                  <td className="py-2 px-2"><button onClick={() => setViewingSession(s)} className="text-blue-600 font-semibold">View</button></td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* UPDATED: AI Questions History Table */}
      <div className="bg-white rounded-lg shadow p-4 mb-6">
        <h2 className="text-lg font-semibold mb-4 text-zinc-900">Latest AI Questions</h2>
        <table className="w-full text-sm text-left">
          <thead>
            <tr className="border-b text-zinc-900 font-semibold">
              <th className="py-2 px-2">Search/Job</th>
              <th className="py-2 px-2">Date</th>
              <th className="py-2 px-2">Action</th>
            </tr>
          </thead>
          <tbody>
            {qnaHistory.length === 0 ? (
              <tr><td colSpan="3" className="py-4 text-center text-black-400">No history.</td></tr>
            ) : (
              qnaHistory.slice(0, 5).map((item) => (
                <tr key={item.id} className="border-b hover:bg-gray-50">
                  <td className="py-2 px-2 text-zinc-900 font-medium truncate max-w-[200px]">
                    {item.title || "Untitled Search"}
                  </td>
                  <td className="py-2 px-2 text-zinc-700">
                    {item.createdAt?.seconds ? new Date(item.createdAt.seconds * 1000).toLocaleDateString() : "-"}
                  </td>
                  <td className="py-2 px-2">
                    <button 
                      onClick={() => { 
                        if (onViewSavedQna && item.sections) {
                          onViewSavedQna(item);
                        } else {
                          onGenerateInterviewQuestions(item.jobDescription);
                          setActiveTab('interviewQuestions');
                        }
                      }} 
                      className="text-emerald-600 font-semibold hover:text-emerald-700"
                    >
                      View Q&A
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {viewingSession && <ViewSessionDetailModal session={viewingSession} onClose={() => setViewingSession(null)} />}
    </div>
  );
}